import { createFileRoute, notFound } from "@tanstack/react-router"
import { Printer } from "lucide-react"

import { Button } from "@/components/ui/button"
import { PropertyFacts } from "@/components/property-display"
import { getPropertyDetailFn } from "@/lib/app.functions"
import { formatPrice, titleCase } from "@/lib/constants"

export const Route = createFileRoute("/brochure/$slug")({
  loader: async ({ params }) => {
    const result = await getPropertyDetailFn({ data: { slug: params.slug } })
    if (!result) throw notFound()
    return result.property
  },
  head: ({ loaderData }) => ({
    meta: [
      {
        title: loaderData
          ? `${loaderData.title} printable brochure`
          : "Printable property brochure",
      },
    ],
  }),
  component: BrochurePage,
})

function BrochurePage() {
  const property = Route.useLoaderData()
  return (
    <article className="mx-auto max-w-5xl bg-white p-6 print:max-w-none print:p-0">
      <div data-no-print className="mb-6 flex justify-end">
        <Button className="field-button" onClick={() => window.print()}>
          <Printer />
          Print or save PDF
        </Button>
      </div>
      <header className="flex flex-wrap items-start justify-between gap-6 border-b-4 border-foreground pb-6">
        <div>
          <p className="field-label">Harbor & Pine · printable brochure</p>
          <h1 className="mt-2 font-heading text-6xl leading-none uppercase">
            {property.title}
          </h1>
          <p className="mt-3 text-sm text-muted-foreground">
            {property.address}, {property.locality}, {property.region}
          </p>
        </div>
        <div className="text-right">
          <p className="field-label">{property.reference}</p>
          <p className="mt-2 font-heading text-3xl font-bold">
            {formatPrice(
              property.price,
              property.transaction,
              property.rentPeriod
            )}
          </p>
          <p className="mt-1 text-xs uppercase">
            {titleCase(property.type)} · {titleCase(property.transaction)}
          </p>
        </div>
      </header>
      {property.images[0] ? (
        <img
          src={property.images[0].url}
          alt={property.images[0].alt}
          className="mt-6 aspect-[16/8] w-full object-cover"
        />
      ) : null}
      <PropertyFacts property={property} />
      <div className="mt-8 grid gap-8 sm:grid-cols-[1.25fr_0.75fr]">
        <section>
          <h2 className="font-heading text-3xl uppercase">Listing note</h2>
          <p className="mt-4 text-sm leading-7 text-muted-foreground">
            {property.description}
          </p>
        </section>
        <section>
          <h2 className="font-heading text-3xl uppercase">Amenities</h2>
          <ul className="mt-4 space-y-2 text-sm">
            {property.amenities.map((item) => (
              <li key={item} className="border-b pb-2">
                {item}
              </li>
            ))}
          </ul>
        </section>
      </div>
      <footer className="mt-10 border-t-2 border-foreground pt-4 text-xs text-muted-foreground">
        Fictional marketplace demo. All properties, addresses, prices, people,
        and activity are synthetic. Not an offer or professional advice.
      </footer>
    </article>
  )
}
